/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import {PackageItem} from "types/entities/PackageItem";
import {useTranslation} from "react-i18next";
import {useForm} from "react-hook-form";
import clsx from "clsx";
import {CombinationOfVariantPackageUpsellItemItemContainer} from "./CombinationOfVariantPackageUpsellItemItemContainer";
import {
    CombinationOfVariantAssignedUpsell,
} from "pages/Front/Booking/components/PackageUpsell/CombinationOfVariantPackageUpsellItem";

interface CombinationOfVariantPackageUpsellItemItemProps {
    packageItem: PackageItem;
    isSeller?: boolean;
    assignedUpsells: CombinationOfVariantAssignedUpsell[];
    setAssignedUpsells: React.Dispatch<React.SetStateAction<CombinationOfVariantAssignedUpsell[]>>;
}

const CombinationOfVariantPackageUpsellItemItem = (props: CombinationOfVariantPackageUpsellItemItemProps) => {
    const {t} = useTranslation();
    const currentUpsellInfo = props.assignedUpsells.find(upsell => upsell.upsell_id === props.packageItem.id);
    const currentUpsellCount = currentUpsellInfo?.assigned_count ?? 0;
    const {register} = useForm({
        defaultValues: {
            "upsell-count": currentUpsellCount,
        },
        mode: "onChange",
        shouldUnregister: true,
    });
    const [count, setCount] = useState(currentUpsellCount);
    const maximumValue = props.packageItem?.packageItemVariant?.maximum_count ?? 100;
    const minimum_persons_count = 0;

    const setAssigned = (newUpsellCount: number) => {
        const upsellId = props.packageItem.id;
        props.setAssignedUpsells(prev => {
            const existIndex = prev.findIndex(u => u.upsell_id === upsellId);
            if (existIndex > -1) {
                return prev.map(u =>
                    u.upsell_id === upsellId ? {...u, assigned_count: newUpsellCount} : u
                );
            } else {
                return [...prev, {upsell_id: upsellId, assigned_count: newUpsellCount}];
            }
        });
    }

    const setOnBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
        const rawValue = e.target.value.trim();
        const enteredValue = Number(rawValue);

        const minimumValue = props.packageItem?.packageItemVariant?.minimum_count === 0
            ? 0
            : props.packageItem.minimum_persons_count;

        let safeValue = enteredValue;
        if (isNaN(enteredValue) || enteredValue < minimumValue) {
            safeValue = minimumValue;
            setCount(safeValue);
            setAssigned(safeValue);
            return;
        }
        if (enteredValue > maximumValue) {
            safeValue = maximumValue;
            setCount(safeValue);
            setAssigned(safeValue);
            return;
        }

        setCount(safeValue);
        setAssigned(safeValue);
    }

    function setDriversCountHandler(change: number) {
        const upsellId = props.packageItem.id;
        const currentUpsellCount =
            props.assignedUpsells.find(u => u.upsell_id === upsellId)?.assigned_count ?? 0;
        const newUpsellCount = currentUpsellCount + change;

        const minimumValue = props.packageItem?.packageItemVariant?.minimum_count === 0
            ? 0
            : props.packageItem.minimum_persons_count;

        let safeValue = newUpsellCount;
        if (safeValue < minimumValue) {
            safeValue = minimumValue;
        }
        if (safeValue > maximumValue) {
            safeValue = maximumValue;
        }

        setCount(safeValue);
        setAssigned(safeValue);
    }

    const disabled = false;
    const valueButtonClassName =
        (disabled ? "text-gray-700 cursor-not-allowed" : "text-arbo-orange cursor-pointer") +
        " w-8 text-contrast-800 rounded-md hover:no-underline text-2xl text-center flex flex-row justify-center items-center leading-none";

    useEffect(() => {
        setCount(currentUpsellCount);
    }, [props.assignedUpsells]);

    return (
        <CombinationOfVariantPackageUpsellItemItemContainer
            className={"CombinationOfVariantPackageUpsellItemItem"}
            inputs={
                <div
                    className={clsx(props.packageItem.extra.whole_group && "was-hidden", "UpsellInputs flex flex-col md:flex-grow")}>
                    {props.packageItem.extra.max_count === 1 && (
                        <input {...register("upsell-count")} name={"upsell-count"} type={"hidden"}
                               value={props.packageItem.extra.max_count} />
                    )}
                    {props.packageItem.extra.max_count !== 1 && (
                        <label
                            className={clsx("flex flex-row justify-between items-center border-arbo-orange border-2 rounded-3xl h-[38px] xl:h-11 overflow-hidden")}>
                            <span className={"sr-only"}>{t("Count")}</span>
                            <button className={valueButtonClassName} type={"button"}
                                    onClick={() => setDriversCountHandler(-1)}>
                                <span>-</span>
                            </button>
                            <input
                                {...register("upsell-count")}
                                className={
                                    "font-bold w-10 text-gray-900 hover:appearance-none focus:outline-none text-center text-lg md:text-xl py-1.5"
                                }
                                inputMode={"numeric"}
                                max={maximumValue}
                                min={minimum_persons_count}
                                name={"upsell-count"}
                                type={"text"}
                                value={count}
                                onChange={e => setCount(Number(e.target.value))}
                                onBlur={setOnBlurHandler}
                            />
                            <button className={valueButtonClassName} type={"button"}
                                    onClick={() => setDriversCountHandler(+1)}>
                                <span>+</span>
                            </button>
                        </label>
                    )}
                </div>
            }
            packageItem={props.packageItem}
            style={{}}
            quantity={count}
            isSeller={props.isSeller}
        />
    );
};

export default CombinationOfVariantPackageUpsellItemItem;